"use client"
import type { ComponentProps } from "react"
import { tv, type VariantProps } from "tailwind-variants"
import { classNames } from "../../utils"
import {
  Item,
  ItemAction,
  ItemAvatar,
  ItemContent,
  ItemDescription,
  type ItemProps,
  ItemSide,
  ItemTitle,
} from "../Item"
import { TextBody, type TextBodyProps } from "../Text"
import { ListGroup } from "./ListGroup"
import { ListHeader } from "./ListHeader"

export type ListProps = ComponentProps<"ul"> &
  VariantProps<typeof listVariants> & {
    height?: number
  }

const listVariants = tv({
  variants: {
    showBorder: {
      true: classNames(
        "border border-border-2",
        "*:not-last:!border-b *:not-last:border-b-border-2",
      ),
    },
    variant: {
      default: "rounded-xl bg-bg-app",
      condensed: classNames(
        "rounded-xl bg-bg-app",
        "[&_[data-id=Item]]:px-4",
        "[&_[data-id=Item]]:py-2",
        "[&_[data-id=ItemTitle]]:font-normal",
        "[&_[data-id=ItemContent]]:mr-0",
      ),
      // This variant is used within a container that already has border styles
      framed: classNames(
        "rounded-none border-0 bg-inherit *:rounded-none",
        "[&_[data-id=Item]]:rounded-none",
      ),
    },
  },
})

function List({
  className,
  children,
  height,
  showBorder = true,
  variant = "default",
  ...rest
}: ListProps) {
  return (
    <ul
      className={classNames(
        "*:first:rounded-t-xl *:last:rounded-b-xl",
        "[&_[data-id=Item]]:border-0",
        "[&_[data-id=Item]]:overflow-hidden",
        listVariants({ showBorder, variant }),
        className,
      )}
      style={{
        height: height ? `${height}px` : undefined,
        // If a height is set, content should scroll within the container
        overflow: height ? "auto" : undefined,
      }}
      {...rest}
    >
      {children}
    </ul>
  )
}

export function ListItem({ asChild, children, ...props }: ItemProps) {
  return (
    <Item asChild={asChild} {...props}>
      {children}
    </Item>
  )
}
export const ListItemAvatar = ItemAvatar
export const ListItemContent = ItemContent
export const ListItemTitle = ItemTitle
export const ListItemDescription = ItemDescription
export const ListItemSide = ItemSide
export const ListItemAction = ItemAction

export const ListItemValue = ({
  weight = "semibold",
  ...rest
}: TextBodyProps) => {
  return <TextBody weight={weight} {...rest} />
}

/**
 * Renders a list of `Item`s. Can also render groupings of items along with associated headers.
 */
export { List, ListHeader, ListGroup }
